import { BreakpointObserver } from '@angular/cdk/layout';
import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { FINANCE_CASH_COUNTER_PERMISSIONS } from '@core/auth/finance-permissions.config';
import { PermissionContextService } from '@core/auth/permission-context.service';
import { CompanyPlanContextService } from '@core/plan/company-plan-context.service';
import { ADMIN_HANDSET_MEDIA_QUERY } from '@shared/utils/admin-layout-breakpoint';
import { FinanceCashCounterOpenerService } from '@features/finance/public-api';

export type AdminUtilityToolId = 'cash_counter';

export type AdminUtilityTool = {
  readonly id: AdminUtilityToolId;
  readonly icon: string;
  readonly labelKey: string;
};

const CASH_COUNTER_ICON = 'solar:wad-of-money-line-duotone';

const EXPANDED_STORAGE_KEY = 'milinco.admin.utilityRailExpanded';

/** Rutas donde el rail choca con UI a pantalla completa. */
function isUtilityRailBlockedPath(path: string): boolean {
  const p = path.split('?')[0];
  if (p === '/pos/venta' || p.startsWith('/pos/venta/')) {
    return true;
  }
  if (p === '/pos/tablero' || p.startsWith('/pos/tablero/')) {
    return true;
  }
  if (p === '/sales/board' || p.startsWith('/sales/board/')) {
    return true;
  }
  if (p === '/finance/cash-counter' || p.startsWith('/finance/cash-counter/')) {
    return true;
  }
  return false;
}

/**
 * Rail derecho de utilidades rápidas (no páginas de menú).
 * Contador de efectivo: solo si la gema está activa. Otras tools se agregan aquí.
 */
@Injectable({ providedIn: 'root' })
export class AdminUtilityRailService {
  private readonly plan = inject(CompanyPlanContextService);
  private readonly permissions = inject(PermissionContextService);
  private readonly breakpoints = inject(BreakpointObserver);
  private readonly router = inject(Router);
  private readonly cashCounterOpener = inject(FinanceCashCounterOpenerService);

  private readonly expandedSignal = signal(this.readExpandedPreference());

  private readonly currentPath = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url.split('?')[0]),
      startWith(this.router.url.split('?')[0])
    ),
    { initialValue: this.router.url.split('?')[0] }
  );

  private readonly isHandset = toSignal(
    this.breakpoints.observe(ADMIN_HANDSET_MEDIA_QUERY).pipe(map((s) => s.matches)),
    { initialValue: this.breakpoints.isMatched(ADMIN_HANDSET_MEDIA_QUERY) }
  );

  readonly expanded = this.expandedSignal.asReadonly();

  readonly tools = computed((): readonly AdminUtilityTool[] => {
    this.plan.summary();
    this.permissions.permissions();
    const tools: AdminUtilityTool[] = [];
    if (this.canUseCashCounter()) {
      tools.push({
        id: 'cash_counter',
        icon: CASH_COUNTER_ICON,
        labelKey: 'finance.cashCounter.title',
      });
    }
    return tools;
  });

  readonly visible = computed(() => {
    if (this.isHandset()) {
      return false;
    }
    if (this.tools().length === 0) {
      return false;
    }
    return !isUtilityRailBlockedPath(this.currentPath() ?? '');
  });

  toggleExpanded(): void {
    const next = !this.expandedSignal();
    this.expandedSignal.set(next);
    try {
      sessionStorage.setItem(EXPANDED_STORAGE_KEY, next ? '1' : '0');
    } catch {
      /* ignore quota / private mode */
    }
  }

  openTool(id: AdminUtilityToolId): void {
    if (id === 'cash_counter') {
      this.cashCounterOpener.open().subscribe({ error: () => undefined });
    }
  }

  private canUseCashCounter(): boolean {
    if (!this.plan.moduleCashCounterEnabled()) {
      return false;
    }
    return this.permissions.can(
      FINANCE_CASH_COUNTER_PERMISSIONS.show.resource,
      FINANCE_CASH_COUNTER_PERMISSIONS.show.action
    );
  }

  private readExpandedPreference(): boolean {
    try {
      return sessionStorage.getItem(EXPANDED_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }
}
